import React, { Component } from 'react';
import axios from 'axios';
import moment from 'moment';
import Chart from 'chart.js';
import * as XLSX from 'xlsx';
import * as ReactBootStrap from 'react-bootstrap';
import { store } from 'react-notifications-component';
import '../tools/css/InformeFacturas.css';

const ApiURL = process.env.REACT_APP_API_URL;

// Paleta categórica validada (skill de dataviz) para identificar cada "tipo de
// documento" con el mismo color en todas las gráficas del informe.
const PALETA_CATEGORICA = ['#2a78d6', '#eb6834', '#1baf7a', '#eda100', '#e87ba4', '#008300', '#4a3aa7', '#e34948'];
const COLOR_MARCA = '#AD1457';
const COLOR_OTROS = '#c3c2b7';
const TEXTO_MUTED = '#898781';
const TEXTO_SECUNDARIO = '#52514e';
const GRID_COLOR = '#e1e0d9';
const EJE_COLOR = '#c3c2b7';

function notifAtencion(mensaje) {
  return {
    title: 'Atención',
    message: mensaje,
    type: 'danger',
    container: 'top-right',
    animationIn: ['animated', 'fadeIn'],
    animationOut: ['animated', 'fadeOut'],
    dismiss: { duration: 10000, showIcon: true }
  };
}

/**
 * Reporte de facturación — recorre TODA la paginación de
 * GET /api/Invoices/Facturadas (el endpoint solo pagina, no filtra por fecha)
 * para poder aplicar el rango de fechas y el filtro de tipo de documento en
 * el cliente. Por defecto el rango va del primer día del mes en curso hasta
 * hoy, igual que los demás informes del portal.
 */
class ReporteFacturacion extends Component {

  chartEvolucionRef = React.createRef();
  chartTipoCantidadRef = React.createRef();
  chartTipoMontoRef = React.createRef();
  chartContratoRef = React.createRef();

  chartEvolucion = null;
  chartTipoCantidad = null;
  chartTipoMonto = null;
  chartContrato = null;

  // Mapa estable tipo de documento -> color: una vez asignado no cambia,
  // aunque el filtro reduzca los datos (evita "repintar" al filtrar).
  colorPorTipo = {};
  tiposDocumentoVistos = new Set();

  state = {
    cargando: true,
    paginaCargando: 1,
    totalCargadas: 0,
    facturas: [],
    fechaDesde: moment().startOf('month').format('YYYY-MM-DD'),
    fechaHasta: moment().format('YYYY-MM-DD'),
    filtroDocumento: 'todos',
    tiposDocumentoDisponibles: []
  };

  componentDidMount() {
    this.cargarTodasLasFacturas();
  }

  componentWillUnmount() {
    [this.chartEvolucion, this.chartTipoCantidad, this.chartTipoMonto, this.chartContrato].forEach((grafica) => {
      if (grafica) grafica.destroy();
    });
  }

  obtenerFecha = (factura) => factura.date;

  obtenerTipoDocumento = (factura) => factura.documentType || 'Sin tipo';

  obtenerTotal = (factura) => {
    const valor = factura.total ?? factura.value ?? factura.amount ?? factura.valor ?? 0;
    return Number(valor) || 0;
  };

  obtenerContrato = (factura) => {
    if (factura.contract && typeof factura.contract === 'object') return factura.contract.name || 'Sin contrato';
    if (typeof factura.contract === 'string' && factura.contract) return factura.contract;
    if (factura.contrato) return factura.contrato;
    return 'Sin contrato';
  };

  registrarTiposDocumento = (facturas) => {
    facturas.forEach((f) => this.tiposDocumentoVistos.add(this.obtenerTipoDocumento(f)));
  };

  asignarColoresTipo = (facturas) => {
    const tiposOrdenados = Array.from(new Set(facturas.map(this.obtenerTipoDocumento))).sort((a, b) => a.localeCompare(b));
    tiposOrdenados.forEach((tipo) => {
      if (!this.colorPorTipo[tipo]) {
        const indice = Object.keys(this.colorPorTipo).length;
        this.colorPorTipo[tipo] = indice < PALETA_CATEGORICA.length ? PALETA_CATEGORICA[indice] : COLOR_OTROS;
      }
    });
  };

  // El endpoint solo pagina (numeroPagina), no filtra por fecha — por eso se
  // recorre toda la paginación hasta que una página vuelve vacía, acumulando
  // todo en memoria para poder filtrar por fecha/tipo en el cliente.
  cargarTodasLasFacturas = async () => {
    const MAX_PAGINAS = 300; // salvaguarda ante un backend que nunca devuelva una página vacía

    this.colorPorTipo = {};
    this.tiposDocumentoVistos = new Set();
    this.setState({ cargando: true, paginaCargando: 1, totalCargadas: 0, facturas: [] });

    let pagina = 1;
    let acumulado = [];
    let huboError = false;

    try {
      while (pagina <= MAX_PAGINAS) {
        this.setState({ paginaCargando: pagina, totalCargadas: acumulado.length });

        const response = await axios.get(ApiURL + '/api/Invoices/Facturadas?numeroPagina=' + pagina);
        const datos = (response.data && response.data.data) || [];

        if (datos.length === 0) break;

        acumulado = acumulado.concat(datos);
        pagina += 1;
      }
    } catch (error) {
      console.log(error);
      huboError = true;
      store.addNotification(notifAtencion('No se pudo cargar el listado completo de facturas.'));
    }

    this.registrarTiposDocumento(acumulado);

    this.setState({
      facturas: acumulado,
      totalCargadas: acumulado.length,
      tiposDocumentoDisponibles: Array.from(this.tiposDocumentoVistos).sort((a, b) => a.localeCompare(b)),
      cargando: false
    }, () => {
      if (!huboError) this.dibujarGraficas();
    });
  };

  cambiarFechaDesde = (e) => {
    this.setState({ fechaDesde: e.target.value }, () => this.dibujarGraficas());
  };

  cambiarFechaHasta = (e) => {
    this.setState({ fechaHasta: e.target.value }, () => this.dibujarGraficas());
  };

  cambiarFiltroDocumento = (e) => {
    this.setState({ filtroDocumento: e.target.value }, () => this.dibujarGraficas());
  };

  facturasFiltradas = () => {
    const { facturas, fechaDesde, fechaHasta, filtroDocumento } = this.state;
    return facturas.filter((f) => {
      const fecha = this.obtenerFecha(f);

      if (fechaDesde && fecha && moment(fecha).isBefore(moment(fechaDesde, 'YYYY-MM-DD'), 'day')) return false;
      if (fechaHasta && fecha && moment(fecha).isAfter(moment(fechaHasta, 'YYYY-MM-DD'), 'day')) return false;
      if (filtroDocumento !== 'todos' && this.obtenerTipoDocumento(f) !== filtroDocumento) return false;

      return true;
    });
  };

  // Datos de cabecera comunes a los 2 tipos de informe (una factura = un
  // objeto). El detallado repite esto mismo en cada fila de ítem.
  filaCabecera = (factura) => {
    const usuarioPaciente = factura.patient && factura.patient.user ? factura.patient.user : null;
    const nombrePaciente = usuarioPaciente
      ? [usuarioPaciente.name1, usuarioPaciente.name2, usuarioPaciente.lastname1, usuarioPaciente.lastname2].filter(Boolean).join(' ')
      : '';

    return {
      id_documento: factura.id,
      fecha: factura.date,
      tipo_documento: factura.documentType,
      prefijo_factura: factura.prefijo,
      numero_factura: factura.numero,
      Subtotal: factura.subtotal,
      IVA: factura.taxes,
      Total: factura.total,
      Estado_documento: factura.status,
      CUFE: factura.cufe,
      Cliente: factura.payer ? factura.payer.name : '',
      IDCliente: factura.payer ? factura.payer.nit : '',
      Paciente: nombrePaciente,
      Tipoid_paciente: usuarioPaciente ? usuarioPaciente.typeId : '',
      IDPaciente: usuarioPaciente ? usuarioPaciente.identification : ''
    };
  };

  descargarExcel = (filas, sufijoNombre) => {
    const hoja = XLSX.utils.json_to_sheet(filas);
    const libro = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(libro, hoja, 'Facturación');

    const nombreArchivo = `reporte_facturacion_${sufijoNombre}_${this.state.fechaDesde}_a_${this.state.fechaHasta}.xlsx`;
    XLSX.writeFile(libro, nombreArchivo);
  };

  // 1. Informe general: una fila por factura, solo los datos de cabecera.
  exportarInformeGeneral = () => {
    const facturas = this.facturasFiltradas();

    if (facturas.length === 0) {
      store.addNotification(notifAtencion('No hay facturas para exportar con los filtros actuales.'));
      return;
    }

    const filas = facturas.map((factura) => this.filaCabecera(factura));
    this.descargarExcel(filas, 'general');
  };

  // 2. Informe detallado por servicios: cada renglón de "items" de una
  // factura sale como su propia fila, repitiendo los datos de cabecera de
  // esa factura (mismo id_documento/fecha/... en todas las filas que le
  // pertenecen).
  exportarInformeDetallado = () => {
    const facturas = this.facturasFiltradas();

    if (facturas.length === 0) {
      store.addNotification(notifAtencion('No hay facturas para exportar con los filtros actuales.'));
      return;
    }

    const filas = [];

    facturas.forEach((factura) => {
      const cabecera = this.filaCabecera(factura);
      const items = Array.isArray(factura.items) ? factura.items : [];

      if (items.length === 0) {
        filas.push({
          ...cabecera,
          Servicio: '',
          Codigo: '',
          CUPS: '',
          nombre_CUPS: '',
          ID_tipo_servicio: '',
          Tipo_servicio: '',
          Cantidad: '',
          Valor_unitario: '',
          ValorIVA: '',
          totalValue: '',
          Autorizacion: ''
        });
        return;
      }

      items.forEach((item) => {
        const servicio = item.service || {};
        const cups = servicio.cups || {};
        const categoriaRips = servicio.categoriaRips || {};
        filas.push({
          ...cabecera,
          Servicio: servicio.name,
          Codigo: servicio.code,
          CUPS: cups.code,
          nombre_CUPS: cups.name,
          ID_tipo_servicio: categoriaRips.id,
          Tipo_servicio: categoriaRips.nombre,
          Cantidad: item.quantity,
          Valor_unitario: item.unitValue,
          ValorIVA: item.ivaValor,
          totalValue: item.totalValue,
          Autorizacion: item.authorizationNumber
        });
      });
    });

    this.descargarExcel(filas, 'detallado_servicios');
  };

  formatoMoneda = (valor) => '$' + Number(valor || 0).toLocaleString('es-CO', { maximumFractionDigits: 0 });

  dibujarGraficas = () => {
    const facturas = this.facturasFiltradas();
    this.asignarColoresTipo(facturas);
    this.dibujarGraficaEvolucion(facturas);
    this.dibujarGraficaTipoCantidad(facturas);
    this.dibujarGraficaTipoMonto(facturas);
    this.dibujarGraficaContrato(facturas);
  };

  // Line: evolución cronológica del monto facturado, color de marca (un solo eje temporal).
  dibujarGraficaEvolucion = (facturas) => {
    const lienzo = this.chartEvolucionRef.current;
    if (!lienzo) return;

    const totalPorDia = {};
    facturas.forEach((f) => {
      const fecha = this.obtenerFecha(f);
      if (!fecha) return;
      const clave = moment(fecha).format('YYYY-MM-DD');
      totalPorDia[clave] = (totalPorDia[clave] || 0) + this.obtenerTotal(f);
    });

    const clavesOrdenadas = Object.keys(totalPorDia).sort();
    const etiquetas = clavesOrdenadas.map((clave) => moment(clave).format('DD/MM'));
    const datos = clavesOrdenadas.map((clave) => totalPorDia[clave]);

    if (this.chartEvolucion) this.chartEvolucion.destroy();

    this.chartEvolucion = new Chart(lienzo.getContext('2d'), {
      type: 'line',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Total facturado',
          data: datos,
          borderColor: COLOR_MARCA,
          backgroundColor: 'rgba(173, 20, 87, .12)',
          pointBackgroundColor: COLOR_MARCA,
          pointRadius: 3,
          fill: true,
          lineTension: 0.25
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_MUTED, fontSize: 11, maxRotation: 0 }
          }],
          yAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, callback: (valor) => this.formatoMoneda(valor) }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => this.formatoMoneda(item.yLabel)
          }
        }
      }
    });
  };

  // Doughnut: identidad = color (no hay eje), por eso lleva leyenda y paleta categórica.
  dibujarGraficaTipoCantidad = (facturas) => {
    const lienzo = this.chartTipoCantidadRef.current;
    if (!lienzo) return;

    const conteo = {};
    facturas.forEach((f) => {
      const tipo = this.obtenerTipoDocumento(f);
      conteo[tipo] = (conteo[tipo] || 0) + 1;
    });

    const etiquetas = Object.keys(conteo);
    const datos = etiquetas.map((t) => conteo[t]);
    const colores = etiquetas.map((t) => this.colorPorTipo[t] || COLOR_OTROS);

    if (this.chartTipoCantidad) this.chartTipoCantidad.destroy();

    this.chartTipoCantidad = new Chart(lienzo.getContext('2d'), {
      type: 'doughnut',
      data: {
        labels: etiquetas,
        datasets: [{
          data: datos,
          backgroundColor: colores,
          borderColor: '#ffffff',
          borderWidth: 2
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        cutoutPercentage: 62,
        legend: {
          position: 'bottom',
          labels: { boxWidth: 12, fontColor: TEXTO_SECUNDARIO, fontSize: 12, padding: 14 }
        },
        tooltips: {
          callbacks: {
            label: (item, data) => {
              const etiqueta = data.labels[item.index];
              const valor = data.datasets[0].data[item.index];
              return `${etiqueta}: ${valor} documento${valor === 1 ? '' : 's'}`;
            }
          }
        }
      }
    });
  };

  // Bar horizontal: misma identidad de "tipo de documento" que el doughnut, mismos colores estables.
  dibujarGraficaTipoMonto = (facturas) => {
    const lienzo = this.chartTipoMontoRef.current;
    if (!lienzo) return;

    const totalPorTipo = {};
    facturas.forEach((f) => {
      const tipo = this.obtenerTipoDocumento(f);
      totalPorTipo[tipo] = (totalPorTipo[tipo] || 0) + this.obtenerTotal(f);
    });

    const etiquetas = Object.keys(totalPorTipo);
    const datos = etiquetas.map((t) => totalPorTipo[t]);
    const colores = etiquetas.map((t) => this.colorPorTipo[t] || COLOR_OTROS);

    if (this.chartTipoMonto) this.chartTipoMonto.destroy();

    this.chartTipoMonto = new Chart(lienzo.getContext('2d'), {
      type: 'horizontalBar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Total facturado',
          data: datos,
          backgroundColor: colores,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, callback: (valor) => this.formatoMoneda(valor) }
          }],
          yAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_SECUNDARIO, fontSize: 12 }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => this.formatoMoneda(item.xLabel)
          }
        }
      }
    });
  };

  // Bar vertical: magnitud comparada entre contratos ya identificados por el eje,
  // por eso usa un solo color (marca) en vez de la paleta categórica.
  dibujarGraficaContrato = (facturas) => {
    const lienzo = this.chartContratoRef.current;
    if (!lienzo) return;

    const totalesPorContrato = {};
    facturas.forEach((f) => {
      const contrato = this.obtenerContrato(f);
      totalesPorContrato[contrato] = (totalesPorContrato[contrato] || 0) + this.obtenerTotal(f);
    });

    const ordenados = Object.entries(totalesPorContrato).sort((a, b) => b[1] - a[1]);
    const TOP = 10;
    const principales = ordenados.slice(0, TOP);
    const resto = ordenados.slice(TOP);

    if (resto.length) {
      const sumaResto = resto.reduce((acc, [, valor]) => acc + valor, 0);
      principales.push([`Otros (${resto.length})`, sumaResto]);
    }

    const etiquetas = principales.map(([nombre]) => nombre);
    const datos = principales.map(([, valor]) => valor);

    if (this.chartContrato) this.chartContrato.destroy();

    this.chartContrato = new Chart(lienzo.getContext('2d'), {
      type: 'bar',
      data: {
        labels: etiquetas,
        datasets: [{
          label: 'Total facturado',
          data: datos,
          backgroundColor: COLOR_MARCA,
          barPercentage: 0.6,
          categoryPercentage: 0.7
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        legend: { display: false },
        scales: {
          xAxes: [{
            gridLines: { display: false, drawBorder: true, color: EJE_COLOR },
            ticks: { fontColor: TEXTO_MUTED, fontSize: 11, maxRotation: 40, minRotation: 0 }
          }],
          yAxes: [{
            gridLines: { color: GRID_COLOR, drawBorder: false, zeroLineColor: EJE_COLOR },
            ticks: { beginAtZero: true, fontColor: TEXTO_MUTED, callback: (valor) => this.formatoMoneda(valor) }
          }]
        },
        tooltips: {
          callbacks: {
            label: (item) => this.formatoMoneda(item.yLabel)
          }
        }
      }
    });
  };

  render() {
    const facturas = this.facturasFiltradas();
    const totalFacturado = facturas.reduce((acc, f) => acc + this.obtenerTotal(f), 0);
    const cantidad = facturas.length;
    const promedio = cantidad ? totalFacturado / cantidad : 0;
    const diasConFacturacion = new Set(facturas.filter((f) => this.obtenerFecha(f)).map((f) => moment(this.obtenerFecha(f)).format('YYYY-MM-DD'))).size;

    const resumenPorTipo = {};
    facturas.forEach((f) => {
      const tipo = this.obtenerTipoDocumento(f);
      if (!resumenPorTipo[tipo]) resumenPorTipo[tipo] = { cantidad: 0, total: 0 };
      resumenPorTipo[tipo].cantidad += 1;
      resumenPorTipo[tipo].total += this.obtenerTotal(f);
    });

    return (
      <div className="content-wrapper">
        <div className="col-12 col-sm-12">

          <div className="row">
            <div className="col-sm-12">
              <br/>
              <div className="text-center" id="title"><h6><b>REPORTE DE FACTURACIÓN</b></h6></div>
            </div>
          </div>

          <div className="row m24if-filtros">
            <div className="col-sm-3">
              <div className="input-group input-group-sm mb-3">
                <div className="input-group-prepend">
                  <div className="input-group-text">Desde</div>
                </div>
                <input type="date" className="form-control" value={this.state.fechaDesde} onChange={this.cambiarFechaDesde}/>
              </div>
            </div>

            <div className="col-sm-3">
              <div className="input-group input-group-sm mb-3">
                <div className="input-group-prepend">
                  <div className="input-group-text">Hasta</div>
                </div>
                <input type="date" className="form-control" value={this.state.fechaHasta} onChange={this.cambiarFechaHasta}/>
              </div>
            </div>

            <div className="col-sm-4">
              <div className="input-group input-group-sm mb-3">
                <div className="input-group-prepend">
                  <div className="input-group-text">Tipo de documento</div>
                </div>
                <select className="form-control" value={this.state.filtroDocumento} onChange={this.cambiarFiltroDocumento}>
                  <option value="todos">Todos</option>
                  {this.state.tiposDocumentoDisponibles.map((tipo) => (
                    <option key={tipo} value={tipo}>{tipo}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="col-sm-2">
              <button type="button" className="btn btn-outline-primary btn-sm btn-block mb-3" disabled={this.state.cargando} onClick={this.cargarTodasLasFacturas}>
                <i className="fas fa-sync-alt"></i> Actualizar
              </button>
            </div>

            <div className="col-sm-2">
              <div className="btn-group btn-block mb-3">
                <button type="button" className="btn m24if-btn-exportar dropdown-toggle btn-block" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false" disabled={this.state.cargando}>
                  <i className="fas fa-file-excel"></i> Exportar a Excel
                </button>
                <div className="dropdown-menu">
                  <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); this.exportarInformeGeneral(); }}>1. Informe general</a>
                  <a className="dropdown-item" href="#" onClick={(e) => { e.preventDefault(); this.exportarInformeDetallado(); }}>2. Informe detallado por servicios</a>
                </div>
              </div>
            </div>
          </div>

          {this.state.cargando ? (
            <div className="d-flex flex-column align-items-center justify-content-center" style={{padding: '20px 0'}}>
              <ReactBootStrap.Spinner animation="border" />
              <span className="m24if-nota">Cargando facturas (página {this.state.paginaCargando}) — {this.state.totalCargadas.toLocaleString('es-CO')} acumuladas...</span>
            </div>
          ) : false}

          <div className="row m24if-kpis">
            <div className="col-md-3">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Total facturado</span>
                <span className="m24if-stat-value">{this.formatoMoneda(totalFacturado)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Cantidad de documentos</span>
                <span className="m24if-stat-value">{cantidad.toLocaleString('es-CO')}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Promedio por documento</span>
                <span className="m24if-stat-value">{this.formatoMoneda(promedio)}</span>
              </div>
            </div>
            <div className="col-md-3">
              <div className="m24if-stat">
                <span className="m24if-stat-label">Días con facturación</span>
                <span className="m24if-stat-value">{diasConFacturacion.toLocaleString('es-CO')}</span>
              </div>
            </div>
          </div>

          <div className={`row m24if-graficas ${this.state.cargando ? 'm24if-cargando' : ''}`}>

            <div className="col-md-12">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Evolución diaria del total facturado</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap m24if-canvas-wrap-lg">
                    <canvas ref={this.chartEvolucionRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-4">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Documentos por tipo</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap">
                    <canvas ref={this.chartTipoCantidadRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-8">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Total facturado por tipo de documento</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap">
                    <canvas ref={this.chartTipoMontoRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

            <div className="col-md-12">
              <div className="card">
                <div className="card-header"><h3 className="card-title">Total facturado por contrato</h3></div>
                <div className="card-body">
                  <div className="m24if-canvas-wrap m24if-canvas-wrap-lg">
                    <canvas ref={this.chartContratoRef}></canvas>
                  </div>
                </div>
              </div>
            </div>

          </div>

          <div className="row">
            <div className="col-md-12">
              <div className="card table-responsive">
                <div className="card-header"><h3 className="card-title">Resumen por tipo de documento</h3></div>
                <div className="card-body">
                  <table className="table table-bordered table-striped table-hover informes-table">
                    <thead>
                      <tr><th>Tipo de documento</th><th>Cantidad</th><th>Total facturado</th><th>Promedio</th></tr>
                    </thead>
                    <tbody>
                      {Object.keys(resumenPorTipo).map((tipo) => (
                        <tr key={tipo}>
                          <td>{tipo}</td>
                          <td>{resumenPorTipo[tipo].cantidad}</td>
                          <td>{this.formatoMoneda(resumenPorTipo[tipo].total)}</td>
                          <td>{this.formatoMoneda(resumenPorTipo[tipo].total / resumenPorTipo[tipo].cantidad)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>

          <p className="m24if-nota">Este informe recorre todas las páginas de /api/Invoices/Facturadas ({this.state.totalCargadas.toLocaleString('es-CO')} documentos en total) y luego aplica el rango de fechas y el tipo de documento como filtros locales. Usa "Actualizar" para volver a traer los datos desde el servidor.</p>

        </div>
      </div>
    );
  }
}

export default ReporteFacturacion;
